import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AlertsData } from '../api/types.js';
import { AlertsBanner } from '../components/Alerts/AlertsBanner.js';
import { AlertsFilters } from '../components/Alerts/AlertsFilters.js';
import { AlertsSkeleton } from '../components/Alerts/AlertsSkeleton.js';
import { AlertsTable } from '../components/Alerts/AlertsTable.js';
import {
  DEFAULT_ALERT_FILTERS,
  TIMELINES,
  newYorkToday,
  visibleRows,
  type AlertFilters,
} from '../components/Alerts/model.js';
import { AuditBody, AuditFrame } from '../components/Audit/AuditPage.js';
import { loadPersisted, savePersisted } from '../state/persist.js';
import { queries } from '../store/queries.js';
import { useQuery } from '../store/StoreProvider.js';
import { color, fontSize, radius } from '../theme/tokens.js';

/**
 * Alerts (spec 008, blueprint lines 1130–1203): who the audits flag right now, and on what.
 *
 * Built from ONE response (`/api/audit/alerts`) that the store prefetched at sign-in; every filter
 * narrows it in the browser. Three rules this component owns:
 *
 * - **The header is the audit's clock** (FR-AL05) — `AuditFrame`, shared with the Audit page.
 * - **The team link pre-filters ONCE** (FR-AL13): `/alerts?manager=<employee no>` (the weekly
 *   report's link) selects that manager and their leader in the `useState` initialiser — which runs
 *   during render, before the effect that strips the parameter — and is then dropped from the
 *   address, so a refresh or a shared link shows the reader's own filters, not a stale team. The
 *   export-resume pattern (CLAUDE.md): make either half an effect and they race.
 * - **Filters persist for the session** (`state/persist.ts`), like every filter here — never in
 *   `localStorage`, where a months-old combination would greet someone who believes they are on the
 *   defaults.
 */

const CRUMB = 'CYNET Health Commercial';
const STORAGE_KEY = 'alerts.filters';

function sanitize(raw: unknown): AlertFilters | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const str = (v: unknown, fallback: string) => (typeof v === 'string' && v.length < 80 ? v : fallback);
  return {
    leader: str(r.leader, 'all'),
    manager: str(r.manager, 'all'),
    audit: str(r.audit, 'all'),
    offering: r.offering === 'nursing' || r.offering === 'allied' ? r.offering : 'all',
    timeline: TIMELINES.includes(r.timeline as never) ? (r.timeline as AlertFilters['timeline']) : 'all',
  };
}

/** Selects the manager whose employee number the link names, and their (first) leader. */
export function filtersForTeam(data: AlertsData, employeeNo: string): AlertFilters | null {
  const manager = data.managers.find((m) => m.employeeNo !== null && m.employeeNo === employeeNo.trim());
  if (!manager) return null;
  const leader = data.leaders.find((l) => l.managerIds.includes(manager.managerId));
  return { ...DEFAULT_ALERT_FILTERS, leader: leader?.leaderKey ?? 'all', manager: String(manager.managerId) };
}

function AlertsView({ data, pendingTeam }: { data: AlertsData; pendingTeam: string | null }) {
  const [unknownTeam, setUnknownTeam] = useState(false);
  const [filters, setFilters] = useState<AlertFilters>(() => {
    if (pendingTeam) {
      const team = filtersForTeam(data, pendingTeam);
      if (team) return team;
    }
    return loadPersisted(STORAGE_KEY, sanitize) ?? DEFAULT_ALERT_FILTERS;
  });

  useEffect(() => {
    if (pendingTeam && !filtersForTeam(data, pendingTeam)) setUnknownTeam(true);
    // Mount only: the link applies once.
  }, []);
  useEffect(() => savePersisted(STORAGE_KEY, filters), [filters]);

  // An audit chosen in an earlier session that has no open flag today is not an option: fall back.
  const safe = data.audits.some((a) => a.id === filters.audit) ? filters : { ...filters, audit: 'all' };
  const today = useMemo(() => newYorkToday(), []);
  const rows = visibleRows(data, safe, today);

  return (
    <div>
      {unknownTeam ? (
        <div
          role="status"
          style={{
            background: color.noticeBg,
            color: color.noticeInk,
            borderRadius: radius['3xl'],
            padding: '10px 16px',
            marginBottom: 16,
            fontSize: fontSize.captionStrong,
          }}
        >
          That team link names a manager this roster does not have — showing every team.
        </div>
      ) : null}
      <AlertsBanner count={rows.length} mode={data.email.mode} />
      <AlertsFilters data={data} filters={safe} onChange={setFilters} />
      <AlertsTable data={data} rows={rows} audit={safe.audit} today={today} />
    </div>
  );
}

export function Alerts() {
  const state = useQuery(queries.auditAlerts());
  const [params, setParams] = useSearchParams();
  // Read during render (this initialiser), stripped after mount (the effect) — in that order.
  const [pendingTeam] = useState<string | null>(() => params.get('manager'));
  useEffect(() => {
    if (!pendingTeam) return;
    const next = new URLSearchParams(params);
    next.delete('manager');
    setParams(next, { replace: true });
    // Mount only: the link applies once, and stripping it must not re-run on later changes.
  }, [pendingTeam]);

  const clock = state.data?.data?.clock ?? null;
  return (
    <AuditFrame
      crumb={CRUMB}
      title="Alerts"
      envelope={state.data}
      clock={clock}
      revalidating={state.revalidating}
      pending={clock === null && state.loading}
    >
      <AuditBody
        state={state}
        pageName="Alerts"
        plural
        skeleton={<AlertsSkeleton />}
        render={(data) => <AlertsView data={data} pendingTeam={pendingTeam} />}
      />
    </AuditFrame>
  );
}
